import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {GoogleAuthProvider} from 'firebase/auth';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {environment} from './../../environments/environment';
import {StorageKeys} from '../shared-module-files/simpo.constant';

@Injectable({
  providedIn: 'root',
})
export class AuthServiceService {

  /** Refresh the access token this long before it actually expires. */
  private readonly REFRESH_BUFFER_MS = 60_000;

  constructor(
    private http: HttpClient,
    public afAuth: AngularFireAuth // Inject Firebase auth service
  ) {
  }

  // Auth logic to run auth providers
  AuthLogin(provider: any) {
    return this.afAuth['signInWithPopup'](provider)
      .then((result) => {

      })
      .catch((error) => {

      });
  }

  createAccount(data: any) {
    return this.http.post(environment.baseUrl + `staff/staff/business`, data);
  }

  createAccountv2(data: any) {
    return this.http.post(environment.baseUrl + `staff/staff/v2`, data);
  }

  v3CreateAccount(data: any) {
    return this.http.post(environment.baseUrl + `staff/staff/business/v3`, data);
  }

  getDetailsByBusinessId(businessId: any) {
    return this.http.get(
      environment.baseBusinessUrl + `business/${businessId}`
    );
  }

  login(data: any) {
    return this.http.post(environment.baseUrl + `staff/staff/login`, data);
  }

  /**
   * Switch the authenticated staff session to another assigned business.
   * Pass the target business id and the refresh token issued at login
   * (the login response only carries an access token for business[0]).
   * The response has the same shape as login (new token + refreshToken).
   */
  switchBusiness(businessId: string) {
    const refreshToken = localStorage.getItem(StorageKeys.REFRESH_TOKEN) || '';
    return this.http.post(environment.baseUrl + `staff/auth/switch-business`, {
      businessId,
      refreshToken,
    });
  }

  /**
   * Make sure the current access token is scoped to `targetBusinessId`.
   *
   * The login token is only ever valid for the *default* business (business[0]).
   * So the very first time a user opens ANY other business from the list we must
   * still call switch-business — even though they were never "inside" a business.
   * We only skip the call when we have explicit proof the session already matches
   * the target (`tokenBusinessId`, written on every login / switch / refresh).
   */
  ensureBusinessSession(targetBusinessId: string): Observable<any> {
    if (!targetBusinessId) return of(null);
    const sessionBusinessId =
      localStorage.getItem('tokenBusinessId') || localStorage.getItem('businessId');
    if (sessionBusinessId && sessionBusinessId === targetBusinessId) {
      return of(null);
    }
    return this.switchBusiness(targetBusinessId).pipe(
      // switch-business returns the same shape as refresh: it may be wrapped
      // in `data` OR be the bare { accessToken, refreshToken, expiresIn } body.
      tap((res: any) => this.persistAuthSession(res?.data ?? res, targetBusinessId)),
    );
  }

  /**
   * Exchange the stored refresh token for a fresh access + refresh token pair.
   * Response: { data: { accessToken, refreshToken, tokenType, expiresIn, businessId } }
   */
  refreshAccessToken() {
    const refreshToken = localStorage.getItem(StorageKeys.REFRESH_TOKEN) || '';
    return this.http.post(environment.baseUrl + `staff/auth/refresh`, { refreshToken });
  }

  /**
   * Persist the tokens returned by login / switch-business (`token`) or
   * refresh (`accessToken`). Replaces BOTH tokens and stores the absolute
   * moment the access token expires:  Date.now() + expiresIn * 1000
   */
  persistAuthSession(data: any, fallbackBusinessId?: string): void {
    if (!data) return;
    const accessToken = data.accessToken || data.token;

    // Remember which business this token is scoped to, so ensureBusinessSession
    // knows whether a switch is still required.
    const tokenBusinessId =
      data.businessId || data.business?.id || data.business?.[0]?.id || fallbackBusinessId;
    if (tokenBusinessId) {
      localStorage.setItem('tokenBusinessId', tokenBusinessId);
    }

    if (accessToken) {
      localStorage.setItem(StorageKeys.token, accessToken);

      if (data.expiresIn != null) {
        const accessTokenExpiresAt = Date.now() + Number(data.expiresIn) * 1000;
        localStorage.setItem(StorageKeys.ACCESS_TOKEN_EXPIRES_AT, String(accessTokenExpiresAt));
      }

      // keep the interceptor's session token in sync when it is in use
      if (sessionStorage.getItem('setToken') != null) {
        sessionStorage.setItem('setToken', accessToken);
      }
    }

    if (data.refreshToken) {
      localStorage.setItem(StorageKeys.REFRESH_TOKEN, data.refreshToken);
    }

    // login / switch-business responses carry the full user object + business list
    if (data.id || data.business) {
      localStorage.setItem(StorageKeys.LOGGED_IN_USER, JSON.stringify(data));
    }
    if (Array.isArray(data.business)) {
      localStorage.setItem('business', JSON.stringify(data.business));
    }
  }

  /**
   * Epoch milliseconds when the access token expires.
   * Primary: `accessTokenExpiresAt` saved from the login response `expiresIn`.
   * Fallback: the JWT `exp` claim (frontend scheduling only — the Gateway
   * still performs the real verification).
   */
  getAccessTokenExpiresAt(): number | null {
    const stored = Number(localStorage.getItem(StorageKeys.ACCESS_TOKEN_EXPIRES_AT) || 0);
    if (stored) return stored;

    const jwt = localStorage.getItem(StorageKeys.token);
    if (jwt) {
      try {
        const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (typeof payload?.exp === 'number') return payload.exp * 1000;
      } catch (e) { /* ignore */ }
    }
    return null;
  }

  /** Seconds left on the access token (negative once expired), or null if unknown. */
  getAccessTokenRemainingSeconds(): number | null {
    const expiresAt = this.getAccessTokenExpiresAt();
    return expiresAt == null ? null : Math.round((expiresAt - Date.now()) / 1000);
  }

  /**
   * True when the access token is missing, expired, or expires within the
   * refresh buffer (60s):  Date.now() >= accessTokenExpiresAt - 60_000
   */
  isAccessTokenExpiring(): boolean {
    const expiresAt = this.getAccessTokenExpiresAt();
    if (expiresAt == null) return true;
    return Date.now() >= expiresAt - this.REFRESH_BUFFER_MS;
  }


  forgotPassword(data) {
    return this.http.put(
      environment.baseUrl + `staff/staff/password/forgot?email=${data.email}`, data
    )
  }

  resetPassword(data) {
    return this.http.put(
      environment.baseUrl + `staff/staff/password/reset`, data
    )
  }

  changePassword(data) {
    return this.http.put(
      environment.baseUrl + `staff/staff/password`, data
    )
  }

  getAllCountries() {
    return this.http.get(
      environment.serverIp + `master/location/country?size=1000&page=0`
    )
  }

  getCurrencySymbol(businessId: any) {
    return this.http.get(
      environment.baseUrl + `business/business/preference?businessId=${businessId}`
    )
  }
}
